import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Switch, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import apiService from '../services/api.service';
import { showSnackbar } from '../store/slices/uiSlice';
import AccountsRuleEditor, { EditableRule, MatchWindow, NotifyLists } from '../components/accountsNotify/AccountsRuleEditor';

/**
 * Settings › Accounts notifications (Admin). Per company: whether the accounts team is e-mailed
 * when a PO is finally approved (also switched in Company Management), the rules (payment terms,
 * projects, divisions, departments, accountant addresses) and the e-mails sent. The e-mails also
 * show in Settings › SAP Approval Workflows › E-mail log. Backend: services/accountsNotify.service.
 */

type RuleRow = EditableRule & {
  ruleid: number;
  sentence: string;
  matches: number;
  names: { terms: string[]; projects: string[]; divisions: string[]; departments: string[] };
};

type Settings = {
  company: { companyId: number; companyName: string; databaseName: string; approvalSource: 'PRPO' | 'SAP'; enabled: boolean; since: string | null };
  terms: Array<{ code: number; name: string }>;
  covered: number[];
  window: MatchWindow;
  rules: RuleRow[];
};

const errorText = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback;
const money = (currency: string, v: number | null) =>
  v === null || v === undefined ? '-' : `${currency ? `${currency} ` : ''}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const statusColor = (s: string): 'success' | 'error' | 'info' | 'default' => (s === 'Sent' ? 'success' : s === 'Failed' ? 'error' : s === 'Sending' ? 'info' : 'default');

function ConditionChips({ label, values, allText, max }: { label: string; values: string[]; allText: string; max: number }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {!values.length ? <Chip size="small" variant="outlined" label={allText} sx={{ borderStyle: 'dashed' }} /> : null}
        {values.slice(0, max).map((v) => (
          <Tooltip key={v} title={v}><Chip size="small" label={v} sx={{ maxWidth: 260 }} /></Tooltip>
        ))}
        {values.length > max ? (
          <Tooltip title={values.slice(max).join(', ')}><Chip size="small" variant="outlined" label={`+${values.length - max} more`} /></Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function AccountsNotifications() {
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [lists, setLists] = useState<NotifyLists | null>(null);
  const [listsError, setListsError] = useState('');
  const [termCounts, setTermCounts] = useState<Record<string, number> | null>(null);
  const [editing, setEditing] = useState<EditableRule | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [sent, setSent] = useState<any[] | null>(null);
  const [sentLoading, setSentLoading] = useState(false);

  const notify = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => dispatch(showSnackbar({ message, severity }));
  // Ignore answers that arrive after another company was picked.
  const current = useRef<number | ''>('');
  current.current = companyId;

  useEffect(() => {
    apiService.getCompanies(true)
      .then((res: any) => {
        const list = res?.data || [];
        setCompanies(list);
        const preferred = Number(localStorage.getItem('selectedCompanyId') || 0);
        const pick = list.find((c: any) => Number(c.companyid) === preferred) || list[0];
        if (pick) setCompanyId(Number(pick.companyid));
      })
      .catch((e: any) => setError(errorText(e, 'Could not load the companies')));
  }, []);

  const load = useCallback(async (id: number) => {
    setLoading(true); setError('');
    try {
      const res: any = await apiService.getAccountsNotifySettings(id);
      if (current.current === id) setSettings(res?.data || null);
    } catch (e: any) {
      if (current.current === id) setError(errorText(e, 'Could not read the accounts notifications'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSent = useCallback(async (id: number) => {
    setSentLoading(true);
    try {
      const res: any = await apiService.getAccountsNotifySent(id);
      if (current.current === id) setSent(res?.data || []);
    } catch (e: any) {
      notify(errorText(e, 'Could not read the sent e-mails'), 'error');
    } finally {
      setSentLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSettings(null); setLists(null); setListsError(''); setTermCounts(null); setSent(null);
    if (companyId) void load(Number(companyId));
  }, [companyId, load]);

  useEffect(() => {
    if (tab === 1 && companyId && sent === null && !sentLoading) void loadSent(Number(companyId));
  }, [tab, companyId, sent, sentLoading, loadSent]);

  const ensureLists = async (id: number) => {
    if (!lists) {
      apiService.getAccountsNotifyLists(id)
        .then((res: any) => { if (current.current === id) setLists(res?.data || null); })
        .catch((e: any) => setListsError(errorText(e, "Could not read this company's lists")));
    }
    if (!termCounts) {
      apiService.getAccountsNotifyTermCounts(id)
        .then((res: any) => { if (current.current === id) setTermCounts(res?.data?.counts || {}); })
        .catch(() => setTermCounts({}));
    }
  };

  const openEditor = (rule: EditableRule | null) => {
    if (!companyId) return;
    void ensureLists(Number(companyId));
    setEditing(rule);
    setEditorOpen(true);
  };

  const toEditable = (r: RuleRow): EditableRule => ({
    ruleid: r.ruleid, name: r.name, isactive: r.isactive, paymentterms: r.paymentterms,
    projects: r.projects, divisions: r.divisions, departments: r.departments, recipients: r.recipients,
  });

  const switchCompany = async (enabled: boolean) => {
    if (!settings) return;
    const name = settings.company.companyName;
    if (enabled && !window.confirm(`Switch on accounts notifications for ${name}? From now on, every PO finally approved in ${name} with payment terms ticked in an active rule is e-mailed to that rule's recipients.`)) return;
    if (!enabled && !window.confirm(`Switch off accounts notifications for ${name}? No accounts e-mails are sent while it is off; the rules are kept. POs approved while it is off are never e-mailed later.`)) return;
    setBusy('switch');
    try {
      await apiService.setAccountsNotifyEnabled(settings.company.companyId, enabled);
      notify(enabled ? 'Accounts notifications switched on' : 'Accounts notifications switched off');
      await load(settings.company.companyId);
    } catch (e: any) {
      notify(errorText(e, 'Could not switch the accounts notifications'), 'error');
    } finally {
      setBusy('');
    }
  };

  const toggleRule = async (r: RuleRow) => {
    setBusy(`rule-${r.ruleid}`);
    try {
      await apiService.setAccountsNotifyRuleActive(r.ruleid, !r.isactive);
      await load(Number(companyId));
    } catch (e: any) {
      notify(errorText(e, 'Could not change the rule'), 'error');
    } finally {
      setBusy('');
    }
  };

  const removeRule = async (r: RuleRow) => {
    if (!window.confirm(`Delete the rule "${r.name}"? Its recipients stop receiving accounts e-mails for it.`)) return;
    setBusy(`rule-${r.ruleid}`);
    try {
      await apiService.deleteAccountsNotifyRule(r.ruleid);
      notify('Rule deleted');
      await load(Number(companyId));
    } catch (e: any) {
      notify(errorText(e, 'Could not delete the rule'), 'error');
    } finally {
      setBusy('');
    }
  };

  const resend = async (row: any) => {
    setBusy(`resend-${row.notificationid}`);
    try {
      const res: any = await apiService.resendAccountsNotification(Number(row.notificationid));
      const d = res?.data || {};
      notify(d.sent ? 'E-mail sent again' : d.failed ? 'The mail server refused it again: see the reason in the list' : (d.skipped || 'Nothing to send'), d.failed ? 'warning' : 'success');
    } catch (e: any) {
      notify(errorText(e, 'Could not send the e-mail again'), 'error');
    } finally {
      setBusy('');
      if (companyId) await loadSent(Number(companyId));
    }
  };

  const company = settings?.company;
  const terms = settings?.terms || [];
  const covered = new Set(settings?.covered || []);
  const windowText = settings?.window?.from ? `${settings.window.from} – ${settings.window.to}` : '';

  return (
    <Box component="section" aria-labelledby="accounts-notify-heading" sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
        <Box>
          <Typography component="h1" id="accounts-notify-heading" variant="h5" sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}>
            Accounts notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            E-mail the accounts team when a purchase order is finally approved with payment terms that need their action.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 360 } }}>
            <InputLabel id="accounts-notify-company">Company</InputLabel>
            <Select labelId="accounts-notify-company" label="Company" value={companyId} onChange={(e) => setCompanyId(Number(e.target.value))}>
              {companies.map((c) => (
                <MenuItem key={c.companyid} value={Number(c.companyid)}>{c.companyname} ({c.databasename})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => openEditor(null)} disabled={!settings}>Add rule</Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading && !settings ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress aria-label="Loading" /></Box> : null}

      {company ? (
        <Stack spacing={2}>
          <Alert
            severity={company.enabled ? 'success' : 'info'}
            action={(
              <Button color="inherit" size="small" onClick={() => switchCompany(!company.enabled)} disabled={busy === 'switch'}>
                {company.enabled ? 'Switch off' : 'Switch on'}
              </Button>
            )}
          >
            {company.enabled ? (
              <>
                <strong>On for {company.companyName}</strong>
                {company.since ? ` since ${new Date(company.since).toLocaleString()}` : ''}. A PO finally approved with payment terms ticked in an
                active rule is e-mailed to that rule&apos;s recipients. Any other payment terms send nothing.
              </>
            ) : (
              <>
                <strong>Off for {company.companyName}:</strong> no e-mails are sent. You can prepare the rules now and switch notifications on
                here or in Company Management.
              </>
            )}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {company.approvalSource === 'SAP'
                ? 'PO approval of this company follows the SAP B1 approval procedures: the e-mail goes once PRPO sees the final approval in SAP (within 2 minutes), after the PO is created from the approved draft.'
                : 'PO approval of this company runs in PRPO: the e-mail goes at the last approval stage, with the PO as a PDF and its attachments.'}
            </Typography>
          </Alert>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2">
                  Payment terms covered by active rules: {covered.size} of {terms.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">A PO with any other payment terms is not e-mailed.</Typography>
              </Box>
              <Button size="small" onClick={() => setCoverageOpen((v) => !v)}>{coverageOpen ? 'Hide the payment terms' : 'Show the payment terms'}</Button>
            </Stack>
            <Collapse in={coverageOpen}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={1.5}>
                {terms.map((t) => (
                  <Chip
                    key={t.code}
                    size="small"
                    color={covered.has(t.code) ? 'primary' : 'default'}
                    variant={covered.has(t.code) ? 'filled' : 'outlined'}
                    label={t.name}
                    title={covered.has(t.code) ? 'Ticked in an active rule' : 'No active rule: a PO with these terms is not e-mailed'}
                  />
                ))}
              </Stack>
            </Collapse>
          </Paper>

          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={`Rules (${settings?.rules.length || 0})`} />
            <Tab label="Sent e-mails" />
          </Tabs>

          {tab === 0 ? (
            <Stack spacing={1.5}>
              {!settings?.rules.length ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>No rule yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    Add a rule: the payment terms that need the accounts team&apos;s action, the projects, divisions and departments it covers,
                    and who gets the e-mail.
                  </Typography>
                  <Button variant="contained" onClick={() => openEditor(null)}>Add rule</Button>
                </Paper>
              ) : null}
              {settings?.rules.map((r) => (
                <Paper key={r.ruleid} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, opacity: r.isactive ? 1 : 0.75 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      <Switch
                        checked={r.isactive}
                        onChange={() => toggleRule(r)}
                        disabled={busy === `rule-${r.ruleid}`}
                        inputProps={{ 'aria-label': `${r.isactive ? 'Pause' : 'Activate'} the rule ${r.name}` }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{r.name}</Typography>
                      <Chip size="small" color={r.isactive ? 'success' : 'default'} label={r.isactive ? 'Active' : 'Paused'} />
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" onClick={() => openEditor(toEditable(r))}>Edit</Button>
                      <Button size="small" onClick={() => openEditor({ ...toEditable(r), ruleid: undefined, name: `${r.name} (copy)` })}>Duplicate</Button>
                      <Button size="small" color="error" onClick={() => removeRule(r)} disabled={busy === `rule-${r.ruleid}`}>Delete</Button>
                    </Stack>
                  </Stack>
                  <Typography variant="body2" mb={1.5}>{r.sentence}</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 1.5, mb: 1.5 }}>
                    <ConditionChips label={`Payment terms (${r.paymentterms.length})`} values={r.names.terms} allText="" max={3} />
                    <ConditionChips label="Projects" values={r.projects.all ? [] : r.names.projects} allText="All projects" max={2} />
                    <ConditionChips label="Divisions" values={r.divisions.all ? [] : r.names.divisions} allText="All divisions" max={2} />
                    <ConditionChips label="Departments" values={r.departments.all ? [] : r.names.departments} allText="All departments" max={2} />
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 0.5 }}>Send to</Typography>
                      {r.recipients.map((e) => <Chip key={e} size="small" variant="outlined" label={e} />)}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{r.matches}</strong> PO{r.matches === 1 ? '' : 's'} of {windowText || 'the last 90 days'}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
              {settings?.rules.length ? (
                <Typography variant="caption" color="text.secondary">
                  A PO that matches several rules is e-mailed once to each recipient; the e-mail names every rule that matched.
                  {windowText ? ` Counts: this company's ${settings.window.poCount} SAP POs of ${windowText}.` : ''}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Button size="small" onClick={() => companyId && loadSent(Number(companyId))} disabled={sentLoading}>Refresh</Button>
                {sentLoading ? <CircularProgress size={18} aria-label="Loading the sent e-mails" /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                One row per PO per recipient. The same rows show in Settings › SAP Approval Workflows › E-mail log. A failed e-mail keeps the mail
                server&apos;s reason and can be sent again.
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Accounts e-mails sent">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Document</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Payment terms</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(sent || []).map((n) => (
                      <TableRow key={n.notificationid}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(n.lastattempt).toLocaleString()}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {n.source === 'SAP' ? (n.docentry ? `PO ${n.docnum}` : `Draft ${n.docnum || n.draftentry || ''}`) : `PO ${n.docnum || n.poid}`}
                          {n.reviewmode ? <Typography variant="caption" display="block" color="warning.main">review mode</Typography> : null}
                        </TableCell>
                        <TableCell>{n.vendor || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(n.currency, n.doctotal)}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{n.paymentterms || '-'}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>
                          {n.recipientemail}
                          {n.sentto && n.sentto !== n.recipientemail ? <Typography variant="caption" display="block" color="text.secondary">sent to {n.sentto}</Typography> : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{n.rules || '-'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(n.status)} label={n.status} />
                          {n.attempts > 1 ? <Typography variant="caption" display="block" color="text.secondary">{n.attempts} attempts</Typography> : null}
                          {n.error ? <Typography variant="caption" display="block" color={n.status === 'Failed' ? 'error' : 'text.secondary'} sx={{ maxWidth: 360, wordBreak: 'break-word' }}>{n.error}</Typography> : null}
                        </TableCell>
                        <TableCell>
                          {n.status === 'Failed' ? (
                            <Button size="small" onClick={() => resend(n)} disabled={busy === `resend-${n.notificationid}`}>
                              {busy === `resend-${n.notificationid}` ? 'Sending…' : 'Send again'}
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                    {sent && !sent.length ? (
                      <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No accounts e-mail yet for this company.</Typography></TableCell></TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Stack>
      ) : null}

      {company ? (
        <AccountsRuleEditor
          open={editorOpen}
          companyId={company.companyId}
          companyLabel={`${company.companyName} (${company.databaseName})`}
          rule={editing}
          lists={lists}
          listsError={listsError}
          termCounts={termCounts}
          window={settings?.window || null}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); void load(company.companyId); }}
        />
      ) : null}
    </Box>
  );
}
